import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  FINANCING_INVESTORS_QUERY,
  FINANCING_TYPES_QUERY,
} from "@/apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import {
  financingEventsExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";
import { EXPORT_INCLUSION_TYPES, type ExportFieldConfiguration } from "./types";
import {
  DISCOVERED_DATE_OPTIONS,
  DISCOVERED_DATE_RANGE,
  NULL_POLICY,
  NULL_POLICY_OPTIONS,
  NULL_POLICY_UNSET,
  type FinancingEventsFilters,
} from "./financingEvents";

const DATA_TYPE = "Financing Events";

type FinancingTypesPayload = {
  financingTypes: { data: { id: string; name: string }[] };
};

type FinancingInvestorsPayload = {
  financingInvestors: {
    data: { id: string; name: string; hgCompanyId: string; domain: string }[];
  };
};

export type Option = { value: string; label: string };

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  filters: FinancingEventsFilters;
  setFilters: (filters: FinancingEventsFilters) => void;
  fieldConfiguration: ExportFieldConfiguration;
  setFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  enforceMandatoryFields?: boolean;
}

const toNumber = (value: string): number | undefined => {
  if (value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const MultiSelect: React.FC<{
  id: string;
  label: string;
  placeholder: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled: boolean;
  loading: boolean;
}> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled,
  loading,
}) => {
  const selected = useMemo(
    () =>
      value.map(
        (v) => options.find((o) => o.value === v) ?? { value: v, label: v }
      ),
    [options, value]
  );

  return (
    <Autocomplete
      id={id}
      multiple
      disableCloseOnSelect
      size="small"
      options={options}
      loading={loading}
      disabled={disabled}
      value={selected}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(_, next) => onChange(next.map((o) => o.value))}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip key={key} label={opt.label} size="small" {...tagProps} />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ""}
        />
      )}
    />
  );
};

export const NullPolicySelect: React.FC<{
  id: string;
  label: string;
  value: NULL_POLICY | undefined;
  onChange: (policy?: NULL_POLICY) => void;
  disabled: boolean;
}> = ({ id, label, value, onChange, disabled }) => (
  <TextField
    id={id}
    select
    size="small"
    label={label}
    disabled={disabled}
    value={value ?? NULL_POLICY_UNSET}
    onChange={(e) =>
      onChange(
        e.target.value === NULL_POLICY_UNSET
          ? undefined
          : (e.target.value as NULL_POLICY)
      )
    }
  >
    <MenuItem value={NULL_POLICY_UNSET}>No preference</MenuItem>
    {NULL_POLICY_OPTIONS.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

export const DateField: React.FC<{
  id: string;
  label: string;
  value: string | undefined;
  onChange: (next: string) => void;
  disabled: boolean;
}> = ({ id, label, value, onChange, disabled }) => (
  <TextField
    id={id}
    label={label}
    type="date"
    size="small"
    fullWidth
    disabled={disabled}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    slotProps={{ inputLabel: { shrink: true } }}
  />
);

type DiscoveredDate = { start?: string; end?: string };

export const DiscoveredDateFilter: React.FC<{
  idPrefix: string;
  value: DiscoveredDate | undefined;
  onChange: (next: DiscoveredDate | undefined) => void;
  disabled: boolean;
}> = ({ idPrefix, value, onChange, disabled }) => {
  // Held in state rather than derived from the dates: a relative preset
  // writes a start date, indistinguishable from a custom range.
  const [preset, setPreset] = useState<DISCOVERED_DATE_RANGE>(() =>
    value?.start || value?.end
      ? DISCOVERED_DATE_RANGE.CUSTOM
      : DISCOVERED_DATE_RANGE.ANY
  );

  const handlePreset = (next: DISCOVERED_DATE_RANGE) => {
    setPreset(next);

    if (next === DISCOVERED_DATE_RANGE.ANY) {
      onChange(undefined);
      return;
    }

    if (next === DISCOVERED_DATE_RANGE.CUSTOM) {
      onChange({ start: "", end: "" });
      return;
    }

    const days = {
      [DISCOVERED_DATE_RANGE.LAST_7_DAYS]: 7,
      [DISCOVERED_DATE_RANGE.LAST_30_DAYS]: 30,
      [DISCOVERED_DATE_RANGE.LAST_90_DAYS]: 90,
    }[next as Exclude<DISCOVERED_DATE_RANGE, "any" | "custom">];

    const start = new Date();
    start.setDate(start.getDate() - days);

    onChange({ start: start.toISOString().slice(0, 10) });
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Discovered Date</Typography>
      <TextField
        id={`${idPrefix}-discovered-range`}
        select
        size="small"
        label="Date Range"
        disabled={disabled}
        value={preset}
        onChange={(e) => handlePreset(e.target.value as DISCOVERED_DATE_RANGE)}
        inputProps={{ "data-testid": `${idPrefix}-discovered-range-select` }}
      >
        {DISCOVERED_DATE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      {preset === DISCOVERED_DATE_RANGE.CUSTOM && (
        <Stack direction="row" spacing={2}>
          <DateField
            id={`${idPrefix}-discovered-start`}
            label="From"
            value={value?.start}
            onChange={(start) => onChange({ ...value, start })}
            disabled={disabled}
          />
          <DateField
            id={`${idPrefix}-discovered-end`}
            label="To"
            value={value?.end}
            onChange={(end) => onChange({ ...value, end })}
            disabled={disabled}
          />
        </Stack>
      )}
    </Stack>
  );
};

export const FinancingEventsExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  filters,
  setFilters,
  fieldConfiguration,
  setFieldConfiguration,
  enforceMandatoryFields,
}) => {
  const disabled = inclusion !== EXPORT_INCLUSION_TYPES.ALL;

  const { data: typesData, loading: typesLoading } =
    useQuery<FinancingTypesPayload>(FINANCING_TYPES_QUERY, {
      context: { clientName: TAXONOMY_CLIENT },
    });
  const { data: investorsData, loading: investorsLoading } =
    useQuery<FinancingInvestorsPayload>(FINANCING_INVESTORS_QUERY, {
      context: { clientName: TAXONOMY_CLIENT },
    });

  const financingTypeOptions = useMemo<Option[]>(
    () =>
      (typesData?.financingTypes?.data ?? []).map((type) => ({
        value: type.name,
        label: type.name,
      })),
    [typesData]
  );

  const investorOptions = useMemo<Option[]>(
    () =>
      (investorsData?.financingInvestors?.data ?? []).map((investor) => ({
        value: investor.hgCompanyId,
        label: investor.domain
          ? `${investor.name} (${investor.domain})`
          : investor.name,
      })),
    [investorsData]
  );

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        Financing Events
      </Typography>
      <ExportDataTypeInclusionSelector
        dataType={DATA_TYPE}
        options={[
          {
            label: "Do not include any Financing Events",
            value: EXPORT_INCLUSION_TYPES.NONE,
          },
          {
            label: "Include Financing Events",
            value: EXPORT_INCLUSION_TYPES.ALL,
          },
        ]}
        inclusion={inclusion}
        setInclusion={setInclusion}
      />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Financing Type</Typography>
        <MultiSelect
          id="financing-events-type-include"
          label="Include"
          placeholder="Any financing type"
          options={financingTypeOptions}
          value={filters.financingType?.include ?? []}
          onChange={(include) =>
            setFilters({
              ...filters,
              financingType: { ...filters.financingType, include },
            })
          }
          disabled={disabled}
          loading={typesLoading}
        />
        <MultiSelect
          id="financing-events-type-exclude"
          label="Exclude"
          placeholder="Nothing excluded"
          options={financingTypeOptions}
          value={filters.financingType?.exclude ?? []}
          onChange={(exclude) =>
            setFilters({
              ...filters,
              financingType: { ...filters.financingType, exclude },
            })
          }
          disabled={disabled}
          loading={typesLoading}
        />
        <Typography variant="caption" color="text.secondary">
          A type in both lists is excluded.
        </Typography>
        <NullPolicySelect
          id="financing-events-type-null-policy"
          label="Records with no financing type"
          value={filters.financingType?.nullPolicy}
          onChange={(nullPolicy) =>
            setFilters({
              ...filters,
              financingType: { ...filters.financingType, nullPolicy },
            })
          }
          disabled={disabled}
        />
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Amount</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            id="financing-events-amount-min"
            label="Minimum (USD)"
            type="number"
            size="small"
            fullWidth
            disabled={disabled}
            value={filters.amount?.min ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                amount: { ...filters.amount, min: toNumber(e.target.value) },
              })
            }
          />
          <TextField
            id="financing-events-amount-max"
            label="Maximum (USD)"
            type="number"
            size="small"
            fullWidth
            disabled={disabled}
            value={filters.amount?.max ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                amount: { ...filters.amount, max: toNumber(e.target.value) },
              })
            }
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Both bounds are optional. Amounts are in US dollars.
        </Typography>
        <NullPolicySelect
          id="financing-events-amount-null-policy"
          label="Records with no amount"
          value={filters.amount?.nullPolicy}
          onChange={(nullPolicy) =>
            setFilters({
              ...filters,
              amount: { ...filters.amount, nullPolicy },
            })
          }
          disabled={disabled}
        />
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Effective Date</Typography>
        <Stack direction="row" spacing={2}>
          <DateField
            id="financing-events-effective-start"
            label="From"
            value={filters.effectiveDate?.start}
            onChange={(start) =>
              setFilters({
                ...filters,
                effectiveDate: { ...filters.effectiveDate, start },
              })
            }
            disabled={disabled}
          />
          <DateField
            id="financing-events-effective-end"
            label="To"
            value={filters.effectiveDate?.end}
            onChange={(end) =>
              setFilters({
                ...filters,
                effectiveDate: { ...filters.effectiveDate, end },
              })
            }
            disabled={disabled}
          />
        </Stack>
        <Typography variant="caption" color="warning.main">
          Effective date is frequently not reported. Use Discovered Date for a
          more reliable view of recency.
        </Typography>
        <NullPolicySelect
          id="financing-events-effective-null-policy"
          label="Records with no effective date"
          value={filters.effectiveDate?.nullPolicy}
          onChange={(nullPolicy) =>
            setFilters({
              ...filters,
              effectiveDate: { ...filters.effectiveDate, nullPolicy },
            })
          }
          disabled={disabled}
        />
      </Stack>

      <DiscoveredDateFilter
        idPrefix="financing-events"
        value={filters.discoveredDate}
        onChange={(discoveredDate) =>
          setFilters({ ...filters, discoveredDate })
        }
        disabled={disabled}
      />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Investors</Typography>
        <MultiSelect
          id="financing-events-investors"
          label="Investors"
          placeholder="Any investor"
          options={investorOptions}
          value={filters.investors?.include ?? []}
          onChange={(include) =>
            setFilters({ ...filters, investors: { include } })
          }
          disabled={disabled}
          loading={investorsLoading}
        />
        <Typography variant="caption" color="text.secondary">
          Matches events where any selected investor took part.
        </Typography>
      </Stack>

      <Box>
        <FieldConfigurationSelector
          fields={financingEventsExportFields}
          firmographicsFields={firmographicsExportFields}
          value={fieldConfiguration}
          onChange={setFieldConfiguration}
          title="Financing Events Fields"
          checkboxLabel="Export All Financing Events Fields"
          requiredField={
            enforceMandatoryFields
              ? {
                  values: ["id"],
                  note: "Note: Event ID will always be included in your export for proper financing event identification.",
                }
              : undefined
          }
          disabled={disabled}
        />
      </Box>
    </Stack>
  );
};
